import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAlertTriangle } from '@tabler/icons';

// ==============================|| RUNNING BANNER ||============================== //

const RunningBanner = ({ messages = [] }) => {
    const theme = useTheme();

    if (!messages.length) return null;

    const bannerColor = theme.palette.warning.dark;
    const contentColor = '#261A48';
    const hasSingleMessage = messages.length === 1;
    const iterationRunDuration = Math.max(36, messages.length * 24);
    const nextIterationStart = iterationRunDuration * 0.8;
    const animationCycleDuration = nextIterationStart * 2;
    const activeAnimationPercentage = (iterationRunDuration / animationCycleDuration) * 100;
    const iterations = hasSingleMessage ? [0] : [0, 1];

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: bannerColor,
                borderBottom: '1px solid rgba(38,26,72,0.2)',
                overflow: 'hidden',
                height: hasSingleMessage ? 'auto' : '32px',
                minHeight: '32px',
                py: hasSingleMessage ? 1 : 0,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                '&::before': {
                    content: hasSingleMessage ? 'none' : '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '50px',
                    background: `linear-gradient(to right, ${bannerColor}, transparent)`,
                    zIndex: 1,
                    pointerEvents: 'none'
                },
                '&::after': {
                    content: hasSingleMessage ? 'none' : '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '50px',
                    background: `linear-gradient(to left, ${bannerColor}, transparent)`,
                    zIndex: 1,
                    pointerEvents: 'none'
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    justifyContent: hasSingleMessage ? 'center' : 'flex-start',
                    whiteSpace: hasSingleMessage ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    '&:hover > span': {
                        animationPlayState: 'paused'
                    }
                }}
            >
                {iterations.map((iteration) => (
                    <Box
                        key={iteration}
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            position: hasSingleMessage ? 'static' : 'absolute',
                            top: hasSingleMessage ? 'auto' : 0,
                            left: hasSingleMessage ? 'auto' : 0,
                            height: hasSingleMessage ? 'auto' : '100%',
                            alignItems: 'center',
                            opacity: hasSingleMessage ? 1 : 0,
                            animation: hasSingleMessage
                                ? 'none'
                                : `bannerIterationScroll ${animationCycleDuration}s linear ${
                                      iteration * nextIterationStart - iterationRunDuration / 2
                                  }s infinite`,
                            '@keyframes bannerIterationScroll': {
                                '0%': {
                                    transform: 'translateX(100vw)',
                                    opacity: 1
                                },
                                [`${activeAnimationPercentage}%`]: {
                                    transform: 'translateX(-100%)',
                                    opacity: 1
                                },
                                [`${Math.min(activeAnimationPercentage + 0.01, 100)}%`]: {
                                    transform: 'translateX(-100%)',
                                    opacity: 0
                                },
                                '100%': {
                                    transform: 'translateX(-100%)',
                                    opacity: 0
                                }
                            }
                        }}
                    >
                        {messages.map((msg, messageIndex) => (
                            <Box
                                key={messageIndex}
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    color: contentColor,
                                    letterSpacing: '0.3px',
                                    px: 4,
                                    textAlign: hasSingleMessage ? 'center' : 'left',
                                    overflowWrap: hasSingleMessage ? 'anywhere' : 'normal'
                                }}
                            >
                                <IconAlertTriangle
                                    aria-hidden="true"
                                    size={15}
                                    stroke={2}
                                    color={contentColor}
                                    style={{ flexShrink: 0, marginRight: '8px' }}
                                />
                                {msg}
                                {!hasSingleMessage && messageIndex < messages.length - 1 && (
                                    <Box
                                        component="span"
                                        aria-hidden="true"
                                        sx={{
                                            width: '2px',
                                            height: '18px',
                                            ml: 4,
                                            borderRadius: '999px',
                                            backgroundColor: 'rgba(38,26,72,0.35)',
                                            flexShrink: 0
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

RunningBanner.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.string)
};

export default RunningBanner;
